import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const HEADERS = ["Date", "Start", "End", "Name", "Surname", "Email", "Mobile", "City/Village", "YourAvailability", "SuggestedPricePH"];

function rowsFromAvailability(items) {
    return items.map((a) => [
        a.date || "",
        a.start_time || "",
        a.end_time || "",
        a.name || "",
        a.surname || "",
        a.email || "",
        a.mobile || "",
        a.location || "",
        a.notes || "",
        a.SuggestedPricePH || "",
    ]);
}

function downloadBlob(blob, filename) {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

function escapeCsv(v) {
    const s = String(v ?? "");
    if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
}

export function exportCSV(items, filename = "referee-availability.csv") {
    const rows = rowsFromAvailability(items);
    const csv = [HEADERS, ...rows].map((r) => r.map(escapeCsv).join(",")).join("\n");
    downloadBlob(new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" }), filename);
}

function htmlTable(items) {
    const rows = rowsFromAvailability(items);
    const head = HEADERS.map((h) => `<th style="background:#A2D83D;color:#1A2421;padding:8px;border:1px solid #999;text-align:left">${h}</th>`).join("");
    const body = rows
        .map((r) => `<tr>${r.map((c) => `<td style="padding:6px;border:1px solid #999">${String(c ?? "").replace(/[<>&]/g, (m) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;" }[m]))}</td>`).join("")}</tr>`)
        .join("");
    return `<table style="border-collapse:collapse;font-family:Arial,sans-serif;font-size:12px"><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>`;
}

export function exportExcel(items, filename = "referee-availability.xls") {
    const html = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40"><head><meta charset="utf-8"/></head><body>${htmlTable(items)}</body></html>`;
    downloadBlob(new Blob([html], { type: "application/vnd.ms-excel" }), filename);
}

export function exportWord(items, filename = "referee-availability.doc") {
    const html = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40"><head><meta charset="utf-8"/><title>Referee Availability</title></head><body><h2 style="font-family:Arial">Referee Availability</h2>${htmlTable(items)}</body></html>`;
    downloadBlob(new Blob(["\ufeff" + html], { type: "application/msword" }), filename);
}

export function exportPDF(items, filename = "referee-availability.pdf") {
    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.text("Referee Availability", 40, 36);
    autoTable(doc, {
        startY: 56,
        head: [HEADERS],
        body: rowsFromAvailability(items),
        styles: { fontSize: 9, cellPadding: 6 },
        headStyles: { fillColor: [162, 216, 61], textColor: [26, 36, 33] },
        alternateRowStyles: { fillColor: [248, 250, 248] },
        margin: { left: 40, right: 40 },
    });
    doc.save(filename);
}

// ---------- Registered Referees export ----------
const USER_HEADERS = ["Name", "Surname", "Status", "Email", "Year of Birth", "Mobile", "Place of living", "Sport & Experience", "Payment Method", "Slots"];

function sportExperienceString(u) {
    if (Array.isArray(u.sports_experience) && u.sports_experience.length > 0) {
        return u.sports_experience
            .map((s) => {
                const sport = (s.sport || "").trim();
                const years = String(s.years ?? "").trim();
                if (!sport) return null;
                return years ? `${sport} - ${years}` : sport;
            })
            .filter(Boolean)
            .join(", ");
    }
    return (u.interested_sports || "").trim();
}

function paymentMethodsString(u) {
    if (Array.isArray(u.payment_methods) && u.payment_methods.length > 0) {
        return u.payment_methods
            .map((p) => {
                const method = (p.method || "").trim();
                const details = (p.details || "").trim();
                if (!method) return null;
                return details ? `${method} - ${details}` : method;
            })
            .filter(Boolean)
            .join(", ");
    }
    return "";
}

function rowsFromUsers(items) {
    return items.map((u) => [
        u.name || "",
        u.surname || "",
        u.email_verified ? "Verified" : "Pending",
        u.email || "",
        u.birth_year || "",
        u.mobile || "",
        u.location || "",
        sportExperienceString(u),
        paymentMethodsString(u),
        String(u.availability_count ?? 0),
    ]);
}

function htmlTableUsers(items) {
    const rows = rowsFromUsers(items);
    const head = USER_HEADERS.map((h) => `<th style="background:#A2D83D;color:#1A2421;padding:8px;border:1px solid #999;text-align:left">${h}</th>`).join("");
    const body = rows
        .map((r) => `<tr>${r.map((c) => `<td style="padding:6px;border:1px solid #999">${String(c ?? "").replace(/[<>&]/g, (m) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;" }[m]))}</td>`).join("")}</tr>`)
        .join("");
    return `<table style="border-collapse:collapse;font-family:Arial,sans-serif;font-size:12px"><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>`;
}

export function exportUsersCSV(items, filename = "registered-referees.csv") {
    const rows = rowsFromUsers(items);
    const csv = [USER_HEADERS, ...rows].map((r) => r.map(escapeCsv).join(",")).join("\n");
    downloadBlob(new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" }), filename);
}

export function exportUsersExcel(items, filename = "registered-referees.xls") {
    const html = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40"><head><meta charset="utf-8"/></head><body>${htmlTableUsers(items)}</body></html>`;
    downloadBlob(new Blob([html], { type: "application/vnd.ms-excel" }), filename);
}

export function exportUsersWord(items, filename = "registered-referees.doc") {
    const html = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40"><head><meta charset="utf-8"/><title>Registered Referees</title></head><body><h2 style="font-family:Arial">Registered Referees</h2>${htmlTableUsers(items)}</body></html>`;
    downloadBlob(new Blob(["\ufeff" + html], { type: "application/msword" }), filename);
}

export function exportUsersPDF(items, filename = "registered-referees.pdf") {
    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.text("Registered Referees", 40, 36);
    autoTable(doc, {
        startY: 56,
        head: [USER_HEADERS],
        body: rowsFromUsers(items),
        styles: { fontSize: 9, cellPadding: 6 },
        headStyles: { fillColor: [162, 216, 61], textColor: [26, 36, 33] },
        alternateRowStyles: { fillColor: [248, 250, 248] },
        margin: { left: 40, right: 40 },
    });
    doc.save(filename);
}
